import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/dashboard/Sidebar';
import DashboardHeader from '../components/dashboard/DashboardHeader';
import SkillsSelector from '../components/common/SkillsSelector';
import LocationSelector from '../components/common/LocationSelector';
import CategorySelector from '../components/common/CategorySelector';
import api from '../api/api';
import { AuthContext } from '../context/AuthContext';
import PerformanceTrackingService from '../services/PerformanceTrackingService';

const PostJobPage = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const isRecruiter = user?.userType === 'recruiter';
  const [jobDetails, setJobDetails] = useState({
    title: '',
    location: '',
    type: 'Full-time',
    category: '',
    experience: '1-3 years',
    education: '10th',
    salaryMin: '',
    salaryMax: '',
    contactNumber: '',
    description: '',
    responsibilities: '',
    requirements: '',
    benefits: '',
    skills: [],
    isUrgent: false});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setJobDetails(prev => ({ ...prev, [name]: value }));
  };

  const handleContactNumberChange = (e) => {
    let value = e.target.value;
    // Remove all non-digit characters
    value = value.replace(/\D/g, '');
    // Limit to 10 digits
    if (value.length > 10) {
      value = value.slice(0, 10);
    }
    // Store with +91 prefix if 10 digits entered
    const formattedValue = value.length === 10 ? `+91${value}` : value;
    setJobDetails(prev => ({ ...prev, contactNumber: formattedValue }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      // Skills are already in array format
      const formattedJob = {
        ...jobDetails
      };

      // Send the job data to the backend

      const response = await api.post('jobs', formattedJob);

      // Track job posting activity for recruiters
      if (isRecruiter) {
        await PerformanceTrackingService.trackJobPosting({
          id: response.data.job?.id || response.data.data?.job?.id || Date.now().toString(),
          title: formattedJob.title
        });
      }

      // Store the job in localStorage as a backup
      const savedJobs = JSON.parse(localStorage.getItem('savedJobs') || '[]');
      savedJobs.push({
        ...formattedJob,
        id: Date.now(), // Use timestamp as a temporary ID
        createdAt: new Date().toISOString(),
        status: 'Active',
        applicants: []
      });
      localStorage.setItem('savedJobs', JSON.stringify(savedJobs));
      
      // Show success message and redirect to posted jobs page based on user type
      alert('Job posted successfully! Check the console for details.');
      
      // Redirect to the appropriate posted jobs page based on user type
      if (isRecruiter) {
        navigate('/recruiter/posted-jobs');
      } else {
        navigate('/posted-jobs');
      }
    } catch (err) {
      console.error('Error posting job:', err);
      console.error('Error details:', err.response);
      
      // Check if it's a plan limit error
      if (err.response?.status === 403 && err.response?.data?.error?.includes('Plan limit reached')) {
        setError(
          <div className="space-y-2">
            <p className="font-semibold">{err.response.data.error}</p>
            <p className="text-sm">You need to either:</p>
            <ul className="text-sm list-disc list-inside space-y-1">
              <li>Delete some existing jobs to free up space</li>
              <li><a href="/pricing" className="text-blue-600 underline font-medium">Upgrade your plan</a> for more job postings</li>
            </ul>
          </div>
        );
      } else {
        setError(err.response?.data?.error || 'Failed to post job');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex bg-gray-50 min-h-screen font-sans">
      <Sidebar activeTab="Post Job" setActiveTab={() => {}} />
      <main className="flex-1 p-8 ml-0 md:ml-24">
        <div className="mt-8 bg-white p-8 border border-black">
          <h1 className="text-2xl font-bold text-black mb-6 border-b border-black pb-3">Post a New Job</h1>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <InputField label="Job Title" name="title" value={jobDetails.title} onChange={handleChange} placeholder="e.g., Senior Frontend Developer" required />
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Location <span className="text-red-500">*</span>
                </label>
                <LocationSelector 
                  selectedLocation={jobDetails.location}
                  onChange={(location) => setJobDetails(prev => ({ ...prev, location }))}
                  required={true}
                  placeholder="Select city or work mode..."
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <SelectField label="Job Type" name="type" value={jobDetails.type} onChange={handleChange} options={['Full-time', 'Part-time', 'Contract', 'Internship']} required />
              <div>
                <label className="block text-sm font-medium text-black mb-2">Category <span className="text-red-500">*</span></label>
                <CategorySelector 
                  selectedCategory={jobDetails.category}
                  onChange={(value) => setJobDetails(prev => ({ ...prev, category: value }))}
                  required
                  placeholder="e.g., Software Engineering"
                />
              </div>
              <SelectField label="Experience Level" name="experience" value={jobDetails.experience} onChange={handleChange} options={['0-1 years', '1-3 years', '3-5 years', '5-10 years', '10+ years']} required />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <InputField label="Minimum Salary (LPA)" name="salaryMin" type="number" value={jobDetails.salaryMin} onChange={handleChange} placeholder="e.g., 15" required />
                <InputField label="Maximum Salary (LPA)" name="salaryMax" type="number" value={jobDetails.salaryMax} onChange={handleChange} placeholder="e.g., 25" required />
            </div>

            <div>
                <label className="block text-sm font-medium text-black mb-2">
                  Contact Number <span className="text-red-500">*</span>
                </label>
                <div className="flex items-center">
                  <span className="px-3 py-2 bg-gray-100 border-b-2 border-gray-300 text-gray-700 font-medium">+91</span>
                  <input
                    type="tel"
                    name="contactNumber"
                    value={jobDetails.contactNumber.replace(/^\+91/, '')}
                    onChange={handleContactNumberChange}
                    placeholder="10-digit mobile number"
                    pattern="[0-9]{10}"
                    maxLength="10"
                    required
                    className="flex-1 px-3 py-2 border-b-2 border-gray-300 focus:border-yellow-400 focus:outline-none transition-colors"
                  />
                </div>
            </div>

            <TextareaField label="Job Description" name="description" value={jobDetails.description} onChange={handleChange} rows="5" placeholder="Provide a compelling overview of the role and your company." required />
            <TextareaField label="Key Responsibilities" name="responsibilities" value={jobDetails.responsibilities} onChange={handleChange} rows="5" placeholder="List the primary duties and tasks for this role." required />
            <TextareaField label="Requirements" name="requirements" value={jobDetails.requirements} onChange={handleChange} rows="5" placeholder="Specify the qualifications, experience, and skills required." required />
            <TextareaField label="Benefits" name="benefits" value={jobDetails.benefits} onChange={handleChange} rows="4" placeholder="Highlight perks, benefits, and what makes your company great." required />
            <SelectField label="Education Qualification" name="education" value={jobDetails.education} onChange={handleChange} options={['10th', '12th', 'Graduate', 'Post Graduate']} required />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Required Skills <span className="text-red-500">*</span>
              </label>
              <SkillsSelector
                selectedSkills={jobDetails.skills}
                onChange={(skills) => setJobDetails(prev => ({ ...prev, skills }))}
                required={true}
                placeholder="Search and add required skills..."
              />
            </div>

            {error && (
              <div className="border border-black p-4 bg-white text-black">
                {error}
              </div>
            )}
            
            <div className="flex items-center justify-between border-t border-black pt-6 mt-6">
              <CheckboxField label="Mark as Urgent Hiring" name="isUrgent" checked={jobDetails.isUrgent} onChange={handleChange} />
              <button 
                type="submit" 
                className="bg-black text-white font-medium py-2 px-6 hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Posting...
                  </>
                ) : 'Post Job'}
              </button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
};

// Helper components for form fields
const InputField = ({ label, required, ...props }) => (
  <div>
    <label className="block text-sm font-medium text-black mb-2">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <input {...props} required={required} className="w-full px-1 py-2 border-b-2 border-gray-300 focus:border-yellow-400 focus:outline-none transition-colors" />
  </div>
);

const SelectField = ({ label, required, options, ...props }) => (
    <div>
        <label className="block text-sm font-medium text-black mb-2">
            {label} {required && <span className="text-red-500">*</span>}
        </label>
        <select {...props} required={required} className="w-full px-1 py-2 border-b-2 border-gray-300 focus:border-yellow-400 focus:outline-none appearance-none bg-white transition-colors">
            {options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
        </select>
    </div>
);

const TextareaField = ({ label, required, ...props }) => (
    <div>
        <label className="block text-sm font-medium text-black mb-2">
            {label} {required && <span className="text-red-500">*</span>}
        </label>
        <textarea {...props} required={required} className="w-full px-1 py-2 border-b-2 border-gray-300 focus:border-yellow-400 focus:outline-none transition-colors"></textarea>
    </div>
);

const CheckboxField = ({ label, name, checked, onChange }) => (
    <div className="flex items-center">
        <input 
            id={name}
            name={name}
            type="checkbox" 
            checked={checked}
            onChange={e => onChange({ target: { name, value: e.target.checked } })}
            className="h-4 w-4 border-black focus:ring-yellow-400 focus:outline-none"
        />
        <label htmlFor={name} className="ml-2 block text-sm text-black">
            {label}
        </label>
    </div>
);

export default PostJobPage;
